import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { downloadTransactions } from 'actions/transactionActions';
import type { FilterValues } from './transactionTypes';
import { PAGINATION_DEFAULTS } from './transactionTypes';
import { trackDownload, trackDownloadInsights, type UserContext } from './transactionTracking';

/**
 * Transaction payload structure for API
 */
interface TransactionPayload {
  page: number;
  pageSize: number;
  [key: string]: any;
}

interface UseTransactionsDownloadProps {
  readonly modalType?: string;
  readonly loggedInUser: any;
  readonly assignedAccountNumber: string;
  readonly getTransactionsPayload: (filterValues: FilterValues) => TransactionPayload;
}

/**
 * Custom hook for managing transactions download functionality
 *
 * RESPONSIBILITIES:
 * - Handle download button interactions
 * - Modify payload for bulk downloads (300 records)
 * - Tracking integration (delegates to transactionTracking)
 */
export const useTransactionsDownload = ({
  modalType,
  loggedInUser,
  assignedAccountNumber,
  getTransactionsPayload,
}: UseTransactionsDownloadProps) => {
  const dispatch = useDispatch();

  // User context for tracking (memoized to prevent unnecessary re-tracking)
  const userContext: UserContext = useMemo(
    () => ({ loggedInUser, assignedAccountNumber }),
    [loggedInUser, assignedAccountNumber]
  );

  /**
   * Handle download button click
   * Creates modified payload for bulk download (300 records, page 0)
   */
  const handleDownload = useCallback(
    (filterValues: FilterValues) => {
      trackDownload(modalType);
      trackDownloadInsights(userContext);

      // Get base payload and modify for download
      const payload = getTransactionsPayload(filterValues);
      payload.page = PAGINATION_DEFAULTS.DOWNLOAD_PAGE;
      payload.pageSize = PAGINATION_DEFAULTS.DOWNLOAD_PAGE_SIZE;

      dispatch(downloadTransactions(payload));
    },
    [modalType, userContext, getTransactionsPayload, dispatch]
  );

  return {
    handleDownload,
  };
};
